import React from 'react';
import L from 'leaflet';
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import MuiTooltip from '@material-ui/core/Tooltip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import LandscapeOutlinedIcon from '@material-ui/icons/LandscapeOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import SpeedIcon from '@material-ui/icons/Speed';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';

import Config from '../../constants/appConfig';
import ImportJobsDialog from './ImportJobsDialog';
import { normalizePreviewGeoJson } from './previewGeoJson';
import './analysis.css';

const COLORS = { navy: '#17324d', blue: '#2c78a8', orange: '#ee7d32', paleBlue: '#e8f2f8' };

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const localDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatNumber = (value, digits = 1) => numberValue(value).toLocaleString('pt-BR', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
});

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(numberValue(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}min`;
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '-';

class JobPreviewMap extends React.Component {
  mapNode = React.createRef();
  map = null;
  layers = null;

  componentDidMount() {
    this.map = L.map(this.mapNode.current, {
      zoomControl: true, attributionControl: false, preferCanvas: true
    }).setView(Config.place, 13);
    L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(this.map);
    this.layers = L.featureGroup().addTo(this.map);
    this.drawData();
    window.setTimeout(() => {
      if (!this.map) return;
      this.map.invalidateSize();
      this.fitToLayers();
    }, 100);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) this.drawData();
  }

  componentWillUnmount() {
    if (this.map) this.map.remove();
  }

  addGeoJson = (geoJson, style) => {
    if (!geoJson || !this.layers) return;
    const normalized = normalizePreviewGeoJson(geoJson);
    if (!normalized) return;
    try { L.geoJSON(normalized, { style: style }).addTo(this.layers); } catch (error) { }
  };

  fitToLayers = () => {
    if (!this.map || !this.layers) return;
    const bounds = this.layers.getBounds();
    if (bounds.isValid()) this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
  };

  drawData = () => {
    if (!this.layers || !this.props.data) return;
    const data = this.props.data;
    this.layers.clearLayers();

    (data.polygons || []).forEach((item) => this.addGeoJson(item.poly || item, {
      color: COLORS.blue, weight: 2, fillColor: COLORS.paleBlue, fillOpacity: 0.2
    }));

    const applications = data.apl && Array.isArray(data.apl.aplicacao) ? data.apl.aplicacao : [];
    applications.forEach((item) => this.addGeoJson(item.poly || item, {
      color: COLORS.orange, weight: 1, fillColor: COLORS.orange, fillOpacity: 0.7
    }));

    this.addGeoJson(data.linha, { color: '#ffffff', weight: 5, opacity: 0.9 });
    this.addGeoJson(data.linha, { color: COLORS.navy, weight: 2, opacity: 1 });

    const base = data.position && Array.isArray(data.position.base) ? data.position.base : null;
    if (base && base.length >= 2) {
      L.circleMarker([base[0], base[1]], {
        radius: 6, color: '#ffffff', weight: 2, fillColor: COLORS.navy, fillOpacity: 1
      }).bindTooltip('Base operacional').addTo(this.layers);
    }

    this.fitToLayers();
  };

  render() {
    return <div ref={this.mapNode} className="job-yield-preview-map" aria-label="Mapa do trabalho executado" />;
  }
}

class YieldAnalysis extends React.Component {
  importPollTimer = null;

  constructor(props) {
    super(props);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    this.state = {
      start, end, items: [], loading: true, error: '', expandedId: null,
      preview: null, previewLoading: false, previewError: '', importOpen: false,
      pendingImports: []
    };
  }

  componentDidMount() { this.loadData(); }

  componentWillUnmount() {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
  }

  reconcilePendingImports = (pendingImports, items) => {
    if (!pendingImports.length) return [];
    const actualJobs = {};
    items.forEach((item) => { actualJobs[String(item.id_job)] = item; });
    const jobs = Object.keys(actualJobs).map((key) => actualJobs[key]);
    const usedJobs = new Set();

    return pendingImports.filter((pending) => {
      const status = String(pending.status || 'processing').toLowerCase();
      if (status === 'processing' || status === 'canceled') return true;

      const matchedByTask = pending.id_task && items.find((item) => {
        const itemTask = item.id_view_task
          || (item.job_link_type === 'processing' ? item.id_task : null);
        return itemTask && String(itemTask) === String(pending.id_task)
          && item.job_link_type !== 'processing';
      });
      if (matchedByTask) {
        usedJobs.add(String(matchedByTask.id_job));
        return false;
      }

      const candidates = jobs.filter((job) => (
        !pending.baselineIds.includes(String(job.id_job)) && !usedJobs.has(String(job.id_job))
      ));
      if (!candidates.length) return Date.now() - pending.created < 30 * 60 * 1000;

      const pendingTitle = String(pending.title || '').trim().toLowerCase();
      const matched = candidates.find((job) => String(job.title || '').trim().toLowerCase() === pendingTitle)
        || candidates[0];
      usedJobs.add(String(matched.id_job));
      return false;
    });
  };

  mergeServerPendingImports = (pendingImports, items) => {
    const merged = pendingImports.slice();
    const knownTasks = new Set(merged.filter((item) => item.id_task)
      .map((item) => String(item.id_task)));

    items.forEach((item) => {
      const isProcessing = item.job_link_type === 'processing'
        || Number(item.is_processing) === 1
        || String(item.task_status || '').toLowerCase() === 'processing';
      const idTask = item.id_view_task || item.id_task;
      if (!isProcessing || !idTask || knownTasks.has(String(idTask))) return;

      merged.push({
        key: `task-${idTask}`,
        id_task: idTask,
        title: item.title || `Trabalho ${idTask}`,
        created: item.created,
        progress: numberValue(item.progress),
        status: 'processing',
        baselineIds: []
      });
      knownTasks.add(String(idTask));
    });

    return merged;
  };

  scheduleImportRefresh = () => {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
    this.importPollTimer = null;
    const hasProcessing = this.state.pendingImports.some((item) => (
      item.id_task && String(item.status || 'processing').toLowerCase() === 'processing'
    ));
    if (hasProcessing) {
      this.importPollTimer = window.setTimeout(this.pollImportTasks, 3000);
      return;
    }

    const hasFinishedWaitingForData = this.state.pendingImports.some((item) => (
      String(item.status || '').toLowerCase() === 'finished'
    ));
    if (hasFinishedWaitingForData) {
      this.importPollTimer = window.setTimeout(() => this.loadData(true), 3000);
    }
  };

  pollImportTasks = () => {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
    this.importPollTimer = null;
    const requestedIds = new Set(this.state.pendingImports.filter((item) => (
      item.id_task && String(item.status || 'processing').toLowerCase() === 'processing'
    )).map((item) => String(item.id_task)));
    if (!requestedIds.size) return;

    fetch(`${Config.server}/task/view`, {
      method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') }
    })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((tasks) => {
        const taskList = Array.isArray(tasks) ? tasks : [];
        const tasksById = taskList.reduce((result, task) => {
          result[String(task.id)] = task;
          return result;
        }, {});
        let hasFinished = false;

        this.setState((state) => ({
          pendingImports: state.pendingImports.map((item) => {
            if (!item.id_task || !requestedIds.has(String(item.id_task))) return item;
            const task = tasksById[String(item.id_task)];
            if (!task) return { ...item, status: 'canceled' };

            const status = String(task.status || 'processing').toLowerCase();
            if (status === 'finished') hasFinished = true;
            return {
              ...item,
              title: task.title || item.title,
              progress: Math.max(0, Math.min(100, numberValue(task.progress))),
              status
            };
          })
        }), () => {
          const waitingFinishedData = this.state.pendingImports.some((item) => (
            String(item.status || '').toLowerCase() === 'finished'
          ));
          if (hasFinished || waitingFinishedData) this.loadData(true);
          else this.scheduleImportRefresh();
        });
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) {
          this.props.link('/login');
          return;
        }
        this.scheduleImportRefresh();
      });
  };

  handleImported = (pendingItems) => {
    const baselineIds = Object.keys(this.state.items.reduce((ids, item) => {
      ids[String(item.id_job)] = true;
      return ids;
    }, {}));
    const pendingImports = (pendingItems || []).map((item) => ({ ...item, baselineIds }));
    this.setState((state) => ({
      importOpen: false,
      pendingImports: state.pendingImports.concat(pendingImports)
    }), this.scheduleImportRefresh);
  };

  loadData = (background = false) => {
    const isBackground = background === true;
    const { start, end } = this.state;
    if (!isBackground) this.setState({ loading: true, error: '', expandedId: null, preview: null });
    const apiUrl = `${Config.server}/job_view/period/${start.getTime()}_${end.getTime()}`;
    fetch(apiUrl, { method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') } })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((items) => {
        const loadedItems = Array.isArray(items) ? items : [];
        this.setState((state) => {
          const reconciled = this.reconcilePendingImports(state.pendingImports, loadedItems);
          return {
            items: loadedItems,
            loading: false,
            pendingImports: this.mergeServerPendingImports(reconciled, loadedItems)
          };
        }, this.scheduleImportRefresh);
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) return this.props.link('/login');
        this.setState({
          loading: false,
          error: isBackground ? this.state.error : 'Nao foi possivel carregar os dados do periodo.'
        }, this.scheduleImportRefresh);
      });
  };

  changeStart = (event) => this.setState({ start: new Date(`${event.target.value}T00:00:00`) });
  changeEnd = (event) => this.setState({ end: new Date(`${event.target.value}T23:59:59.999`) });

  togglePreview = (item) => {
    if (this.state.expandedId === item.id_job) {
      this.setState({ expandedId: null, preview: null, previewError: '' });
      return;
    }
    this.setState({ expandedId: item.id_job, preview: null, previewLoading: true, previewError: '' });
    fetch(`${Config.server}/job_view/preview/${item.id}`, {
      method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') }
    })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((preview) => this.setState({ preview, previewLoading: false }))
      .catch(() => this.setState({
        previewLoading: false,
        previewError: 'A pre-visualizacao deste trabalho nao esta disponivel.'
      }));
  };

  getExecutedItems = () => this.state.items.filter((item) => item.job_link_type === 'appraisal');

  getComparisonItems = (includePending = true) => {
    const jobs = {};
    const trackedTaskIds = new Set(this.state.pendingImports.filter((item) => item.id_task)
      .map((item) => String(item.id_task)));
    this.state.items.forEach((item) => {
      const itemViewTask = item.id_view_task || (item.job_link_type === 'processing' ? item.id_task : null);
      if (itemViewTask && trackedTaskIds.has(String(itemViewTask))) return;

      const key = String(item.id_job);
      const isProcessing = item.job_link_type === 'processing'
        || Number(item.is_processing) === 1
        || String(item.task_status || '').toLowerCase() === 'processing';
      const isCanceled = String(item.task_status || '').toLowerCase() === 'canceled';
      if (!jobs[key]) {
        jobs[key] = {
          id_job: item.id_job,
          title: item.title,
          created: item.created,
          planned: null,
          executed: null,
          pending: isProcessing,
          canceled: isCanceled,
          tracking: isCanceled,
          progress: numberValue(item.progress),
          id_task: itemViewTask
        };
      }
      if (isProcessing) jobs[key].pending = true;
      if (isCanceled) {
        jobs[key].canceled = true;
        jobs[key].tracking = true;
      }
      if (numberValue(item.progress) > numberValue(jobs[key].progress)) jobs[key].progress = numberValue(item.progress);
      if (!jobs[key].id_task && itemViewTask) jobs[key].id_task = itemViewTask;
      if (item.job_link_type === 'appraisal' && !jobs[key].executed) jobs[key].executed = item;
      if (item.job_link_type === 'planejamento' && !jobs[key].planned) jobs[key].planned = item;
      if (new Date(item.created) > new Date(jobs[key].created)) jobs[key].created = item.created;
    });

    const comparisons = Object.keys(jobs)
      .map((key) => jobs[key])
      .sort((a, b) => new Date(b.created) - new Date(a.created));
    if (!includePending) return comparisons;

    return this.state.pendingImports.map((item) => {
      const status = String(item.status || 'processing').toLowerCase();
      return {
        id_job: item.key,
        id_task: item.id_task,
        title: item.title,
        created: item.created,
        planned: null,
        executed: null,
        pending: status === 'processing',
        canceled: status === 'canceled',
        finished: status === 'finished',
        progress: numberValue(item.progress),
        tracking: true
      };
    }).concat(comparisons);
  };

  getTaskLabel = (item) => {
    if (item.canceled) return 'Cancelado';
    if (item.finished) return 'Concluido (100%)';
    return `Processando... ${Math.round(numberValue(item.progress))}%`;
  };

  getStats = () => {
    const executed = this.getExecutedItems();
    const area = executed.reduce((total, item) => total + numberValue(item.area_aplicada), 0);
    const totalTime = executed.reduce((total, item) => total + numberValue(item.tempo_total), 0);
    const performance = executed.length
      ? executed.reduce((total, item) => total + numberValue(item.rendimento), 0) / executed.length : 0;
    return { operations: executed.length, area, totalTime, performance };
  };

  getTrendData = () => this.getComparisonItems(false).slice().reverse().map((item) => ({
    name: new Date(item.created).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    Planejado: Number(numberValue(item.planned && item.planned.rendimento).toFixed(2)),
    Executado: Number(numberValue(item.executed && item.executed.rendimento).toFixed(2))
  }));

  getTimeData = () => {
    const average = (type, field) => {
      const filtered = this.state.items.filter((item) => item.job_link_type === type);
      if (!filtered.length) return 0;
      return Number((filtered.reduce((sum, item) => sum + numberValue(item[field]), 0) / filtered.length / 60).toFixed(1));
    };
    return [
      { name: 'Aplicacao', Executado: average('appraisal', 'tempo_aplicacao'), Planejado: average('planejamento', 'tempo_aplicacao') },
      { name: 'Translado', Executado: average('appraisal', 'tempo_translado'), Planejado: average('planejamento', 'tempo_translado') },
      { name: 'Balao', Executado: average('appraisal', 'tempo_balao'), Planejado: average('planejamento', 'tempo_balao') },
      { name: 'Solo', Executado: average('appraisal', 'tempo_solo'), Planejado: average('planejamento', 'tempo_solo') }
    ];
  };

  renderKpis = () => {
    const stats = this.getStats();
    const cards = [
      { label: 'Operacoes', value: stats.operations, detail: 'trabalhos executados', icon: <FlightTakeoffIcon /> },
      { label: 'Area aplicada', value: `${formatNumber(stats.area)} ha`, detail: 'acumulado no periodo', icon: <LandscapeOutlinedIcon /> },
      { label: 'Rendimento medio', value: `${formatNumber(stats.performance)} ha/h`, detail: 'media das operacoes', icon: <SpeedIcon /> },
      { label: 'Tempo de operacao', value: formatDuration(stats.totalTime), detail: 'tempo total acumulado', icon: <AccessTimeIcon /> }
    ];
    return <div className="job-yield-kpis">{cards.map((card) => (
      <Paper className="job-yield-kpi" elevation={0} key={card.label}>
        <div className="job-yield-kpi-icon">{card.icon}</div>
        <div><span>{card.label}</span><strong>{card.value}</strong><small>{card.detail}</small></div>
      </Paper>
    ))}</div>;
  };

  renderCharts = () => (
    <div className="job-yield-charts">
      <Paper className="job-yield-chart-card" elevation={0}>
        <div className="job-yield-section-title">
          <div><span>Rendimento por operacao</span><small>Comparativo planejado versus executado, em ha/h</small></div>
          <AssessmentOutlinedIcon />
        </div>
        <div className="job-yield-chart"><ResponsiveContainer width="100%" height="100%">
          <AreaChart data={this.getTrendData()} margin={{ top: 12, right: 10, left: -16, bottom: 0 }}>
            <defs><linearGradient id="areaApplied" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.35} />
              <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.02} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7edf2" />
            <XAxis dataKey="name" tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} ha/h`]} />
            <Legend />
            <Area type="monotone" dataKey="Planejado" stroke={COLORS.orange} strokeWidth={2} fill="transparent" />
            <Area type="monotone" dataKey="Executado" stroke={COLORS.blue} strokeWidth={2} fill="url(#areaApplied)" />
          </AreaChart>
        </ResponsiveContainer></div>
      </Paper>
      <Paper className="job-yield-chart-card" elevation={0}>
        <div className="job-yield-section-title">
          <div><span>Composicao do tempo</span><small>Media executada versus planejada, em minutos</small></div>
          <AccessTimeIcon />
        </div>
        <div className="job-yield-chart"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={this.getTimeData()} margin={{ top: 12, right: 10, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7edf2" />
            <XAxis dataKey="name" tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} min`]} /><Legend />
            <Bar dataKey="Executado" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Planejado" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div>
      </Paper>
    </div>
  );

  renderTimeDetails = (planned, executed) => {
    const fields = [
      ['Total', 'tempo_total'], ['Aplicacao', 'tempo_aplicacao'],
      ['Translado', 'tempo_translado'], ['Balao', 'tempo_balao'], ['Solo', 'tempo_solo']
    ];
    const renderGroup = (title, values) => <div className="job-yield-time-tooltip-group">
      <strong>{title}</strong>
      {fields.map(([label, field]) => <span key={field}>{label}: {values ? formatDuration(values[field]) : '-'}</span>)}
    </div>;

    return <div className="job-yield-time-tooltip">
      {renderGroup('Planejado', planned)}
      {renderGroup('Executado', executed)}
    </div>;
  };

  renderMetric = (planned, executed, field, formatter, options = {}) => {
    const plannedValue = planned ? numberValue(planned[field]) : null;
    const executedValue = executed ? numberValue(executed[field]) : null;
    const rawVariation = plannedValue !== null && executedValue !== null && plannedValue !== 0
      ?  ((executedValue - plannedValue) / plannedValue) * 100
      : null;
    // O percentual sempre representa ganho: rendimento maior e tempo menor
    // aparecem positivos; uma piora aparece negativa.
    const variation = rawVariation !== null && options.better === 'lower'
      ? rawVariation * -1
      : rawVariation;
    const isWorse = variation !== null && variation < 0;
    const variationElement = <small className={isWorse ? 'negative' : ''}>
      {variation === null ? 'Sem comparativo' : `${variation > 0 ? '+' : ''}${formatNumber(variation)}%`}
    </small>;

    return <div className="job-yield-comparison-cell">
      <span className="planned"><i />{plannedValue === null ? '-' : formatter(plannedValue)}</span>
      <span className="executed"><i />{executedValue === null ? '-' : formatter(executedValue)}</span>
      {options.showVariation !== false && (options.timeDetails
        ? <MuiTooltip title={this.renderTimeDetails(planned, executed)} arrow placement="top">
          {variationElement}
        </MuiTooltip>
        : variationElement)}
    </div>;
  };

  formatStrategy = (value) => {
    const names = {
      BACKTOBACK: 'Back to Back', SKIPBACKTOBACK: 'Skip Back to Back',
      RACETRACK: 'Racetrack', QUICKRACETRACK: 'Quick Racetrack',
      REVERSERACETRACK: 'Reverse Racetrack', EXPAND: 'Expand', SQUEEZE: 'Squeeze'
    };
    if (!value) return '-';
    return String(value).split(',').map((strategy) => {
      const normalized = strategy.trim().toUpperCase();
      return names[normalized] || strategy.trim();
    }).join(', ');
  };

  renderStrategy = (planned, executed) => <div className="job-yield-comparison-cell job-yield-strategy">
    <span className="planned"><i />{this.formatStrategy(planned && planned.estrategia)}</span>
    <span className="executed"><i />{this.formatStrategy(executed && executed.estrategia)}</span>
  </div>;

  renderPreview = (item) => (
    <TableRow className="job-yield-expanded-row"><TableCell colSpan={8}>
      <div className="job-yield-preview">
        <div className="job-yield-preview-head">
          <div><strong>Pre-visualizacao da operacao</strong><span>{item.title}</span></div>
          <div className="job-yield-map-legend">
            <span><i className="boundary" />Area do trabalho</span>
            <span><i className="application" />Faixa aplicada</span>
            <span><i className="path" />Trajetoria</span>
          </div>
        </div>
        {this.state.previewLoading && <div className="job-yield-preview-state"><CircularProgress size={28} /></div>}
        {this.state.previewError && <div className="job-yield-preview-state error">{this.state.previewError}</div>}
        {this.state.preview && <JobPreviewMap data={this.state.preview} />}
      </div>
    </TableCell></TableRow>
  );

  renderTable = () => (
    <Paper className="job-yield-table-card" elevation={0}>
      <div className="job-yield-section-title table-title">
        <div><span>Planejado x Executado</span><small>{this.getComparisonItems().length} trabalhos no periodo — clique em uma linha para visualizar o mapa</small></div>
        <div className="job-yield-comparison-legend">
          <span><i className="planned" />Planejado</span>
          <span><i className="executed" />Executado</span>
        </div>
      </div>
      <div className="job-yield-table-scroll"><Table className="job-yield-table">
        <TableHead><TableRow>
          <TableCell>Trabalho</TableCell><TableCell>Data / Previa</TableCell>
          <TableCell>Area</TableCell><TableCell>Tempo total</TableCell>
          <TableCell>Rendimento</TableCell><TableCell>Vel. media</TableCell>
          <TableCell>Estrategia</TableCell>
          <TableCell align="center">Acoes</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {this.getComparisonItems().map((item) => {
            const canPreview = item.executed && Number(item.executed.has_preview) === 1;
            const hasTaskState = item.tracking || item.pending;
            const taskLabel = this.getTaskLabel(item);
            return <React.Fragment key={item.id_job}>
              <TableRow
                hover={canPreview}
                className={`${this.state.expandedId === item.id_job ? 'selected' : ''} ${canPreview ? 'preview-enabled' : ''} ${item.pending ? 'processing' : ''} ${item.canceled ? 'canceled' : ''}`}
                onClick={() => { }/*canPreview && this.togglePreview(item.executed)*/}
              >
                <TableCell><strong className="job-yield-job-title">{item.title || `Trabalho #${item.id_job}`}</strong><small className={`job-yield-job-id ${item.pending ? 'processing' : ''} ${item.canceled ? 'canceled' : ''}`}>{hasTaskState ? taskLabel : `#${item.id_job}`}</small></TableCell>
                <TableCell>
                  <div className="job-yield-date-preview">
                    {item.pending ? <CircularProgress size={20} /> : <VisibilityOutlinedIcon fontSize="small" />}
                    <div><strong>{formatDate(item.created)}</strong><small>{hasTaskState ? taskLabel : (canPreview ? 'Clique para abrir o mapa' : 'Processamento indisponivel')}</small></div>
                  </div>
                </TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'area_aplicada', (value) => `${formatNumber(value)} ha`, { showVariation: false })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'tempo_total', formatDuration, { better: 'lower', timeDetails: true })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'rendimento', (value) => `${formatNumber(value)} ha/h`, { better: 'higher' })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'velocidade_media', (value) => `${formatNumber(value)} km/h`, { showVariation: false })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderStrategy(item.planned, item.executed)}</TableCell>
                <TableCell align="center"><div className="job-yield-actions">
                  {item.planned && item.planned.job_link_guid && <Button
                    component="a"
                    href={`/app/estimativa/edit/${item.planned.job_link_guid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    className="job-yield-link-button planned"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={(event) => event.stopPropagation()}
                  >Planejamento</Button>}
                  {item.executed && item.executed.job_link_guid && <Button
                    component="a"
                    href={`/app/avaliacao/${item.executed.job_link_guid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    className="job-yield-link-button"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={(event) => event.stopPropagation()}
                  >Avaliacao</Button>}
                  {!item.executed && !item.planned && (hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : '-')}
                </div></TableCell>
              </TableRow>
              {this.state.expandedId === item.id_job && this.renderPreview(item)}
            </React.Fragment>;
          })}
          {!this.state.loading && !this.getComparisonItems().length && <TableRow><TableCell colSpan={8} className="job-yield-empty">Nenhum registro encontrado para o periodo selecionado.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
      {this.state.loading && <div className="job-yield-loading"><CircularProgress size={30} /></div>}
      {this.state.error && <div className="job-yield-error">{this.state.error}</div>}
    </Paper>
  );

  render() {
    return <main className="job-yield-page">
      <section className="job-yield-hero">
        <div className="job-yield-heading">
          <h1>Analise de Rendimento</h1>
        </div>
        <div className="job-yield-hero-actions">
          <Button
            variant="outlined" className="job-yield-import-button"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => this.setState({ importOpen: true })}
          >Importar trabalhos</Button>
          <Paper className="job-yield-filter" elevation={0}>
            <label><span>Data inicial</span><input type="date" value={localDateValue(this.state.start)} onChange={this.changeStart} max={localDateValue(this.state.end)} /></label>
            <div className="job-yield-date-divider" />
            <label><span>Data final</span><input type="date" value={localDateValue(this.state.end)} onChange={this.changeEnd} min={localDateValue(this.state.start)} /></label>
            <Button variant="contained" className="job-yield-filter-button" startIcon={<RefreshIcon />} onClick={() => this.loadData()} disabled={this.state.loading}>Atualizar</Button>
          </Paper>
        </div>
      </section>
      {this.renderKpis()}{this.renderCharts()}{this.renderTable()}
      <ImportJobsDialog
        open={this.state.importOpen}
        onClose={() => this.setState({ importOpen: false })}
        onImported={this.handleImported}
        link={this.props.link}
      />
    </main>;
  }
}

export default YieldAnalysis;
